import { Heading } from "@/components/ui/heading";
import { CloseIcon, Icon } from "@/components/ui/icon";
import {
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { VStack } from "@/components/ui/vstack";
import { Text } from "@/components/ui/text";
import { WalletDTO } from "@/generated/api/models";
import { useSumFormatter } from "@/utils/useSumFormatter";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Input, InputField, InputSlot } from "@/components/ui/input";
import { parseCurrencyInput } from "@/utils/currency.parser";
import { getI18n } from "react-i18next";
import { centsToValue } from "@/utils/currency.precision";
import { Keyboard } from "react-native";
import { WalletPayoutAlerts } from "./wallet.payout.alerts";
import { Button, ButtonText } from "@/components/ui/button";

export function WalletPayoutModalAmount({
  wallet,
  onClose,
  onContinue,
}: {
  wallet: WalletDTO;
  onClose: () => void;
  onContinue: (amount: number) => void;
}) {
  const availableBalance = wallet.balance + wallet.holdBalance;
  const { formatCents, precision, currencySymbol } = useSumFormatter({
    asset: wallet.currency,
  });

  const minPayoutValue = Math.pow(10, precision);

  const handleKeyPress = () => {
    Keyboard.dismiss();
  };

  const locale = getI18n().language;

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "decimal",
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }),
    [precision]
  );

  const initialValue = Math.max(wallet.balance, minPayoutValue);

  const createValue = useCallback((value: number) => {
    return {
      value: value,
      valueString: formatter.format(centsToValue(value, precision)),
    };
  }, []);

  const [inputValue, setInputValue] = useState(createValue(initialValue));

  const inputValueValidMinimum = inputValue.value >= minPayoutValue;
  const inputValueValidMaximum = inputValue.value <= availableBalance;
  const isValid = inputValueValidMinimum && inputValueValidMaximum;

  return (
    <>
      <ModalHeader>
        <Heading>Request payout</Heading>
        <ModalCloseButton className="self-start mt-2">
          <Icon
            as={CloseIcon}
            size="md"
            className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
          />
        </ModalCloseButton>
      </ModalHeader>
      <ModalBody>
        <VStack className="gap-4">
          <VStack className="gap-2">
            <Input size="md">
              <InputSlot className="pl-1 md:pl-3">
                <Text>{currencySymbol}</Text>
              </InputSlot>
              <InputField
                className="w-full"
                placeholder="Enter amount"
                value={inputValue.valueString}
                onChangeText={(t) => {
                  const newValue =
                    parseCurrencyInput(t, precision) ?? inputValue.value;
                  setInputValue({ valueString: t, value: newValue });
                }}
                onBlur={() => {
                  const newInputValue = {
                    value: inputValue.value,
                    valueString: formatter.format(
                      centsToValue(inputValue.value, precision)
                    ),
                  };
                  setInputValue(newInputValue);
                }}
                onSubmitEditing={handleKeyPress}
                inputMode="decimal"
                returnKeyType="done"
              />
            </Input>
            <Text>
              You may request payout up to: {formatCents(availableBalance)}
            </Text>
          </VStack>
          {!inputValueValidMaximum && (
            <WalletPayoutAlerts.MaximumExceeded
              formatCents={formatCents}
              max={availableBalance}
            />
          )}
          {!inputValueValidMinimum && (
            <WalletPayoutAlerts.InsufficientBalance
              formatCents={formatCents}
              min={minPayoutValue}
            />
          )}
          {isValid && inputValue.value > wallet.balance && (
            <WalletPayoutAlerts.EarlyPayout
              available={Math.max(wallet.balance, 0)}
              formatCents={formatCents}
              onAvailablePress={() => {
                setInputValue(createValue(Math.max(wallet.balance, 0)));
              }}
            />
          )}
        </VStack>
      </ModalBody>

      <ModalFooter className="flex-row items-end">
        <Button onPress={onClose} variant="outline">
          <ButtonText>Cancel</ButtonText>
        </Button>
        <Button
          isDisabled={!isValid || inputValue.value === 0}
          onPress={() => {
            onContinue(inputValue.value);
          }}
        >
          <ButtonText>Continue</ButtonText>
        </Button>
      </ModalFooter>
    </>
  );
}
